import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setUser } from '../../../redux/features/authSlice';
import { getBusinessType } from '../../../utils/businessTypeUtils';
import { getBusinessTypeDisplayName } from '../../../utils/businessTypeConfig';
import { getProfileRoleLabel } from '../../../utils/profileRoleUtils';

const MyProfileView = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  
  // Form fields
  const [businessType, setBusinessType] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  
  useEffect(() => {
    // Get profile data from localStorage and Redux
    const storedProfile = localStorage.getItem('profile');
    const currentBusinessType = getBusinessType();
    
    if (storedProfile) {
      try {
        const parsedProfile = JSON.parse(storedProfile);
        setProfile(parsedProfile);
        
        // Set business type
        if (parsedProfile?.result?.businessType) {
          setBusinessType(parsedProfile.result.businessType);
        } else if (parsedProfile?.businessType) {
          setBusinessType(parsedProfile.businessType);
        } else if (currentBusinessType) {
          setBusinessType(currentBusinessType);
        }
        
        // Set other fields
        if (parsedProfile?.result) {
          setCompanyName(parsedProfile.result.companyName || '');
          setUserRole(parsedProfile.result.role || 'owner');
          setOwnerName(
            parsedProfile.result.ownerName ||
            (parsedProfile.result.firstName && parsedProfile.result.lastName
              ? `${parsedProfile.result.firstName} ${parsedProfile.result.lastName}`
              : parsedProfile.result.firstName || '')
          );
          setEmail(parsedProfile.result.email || '');
          setPhone(parsedProfile.result.phone || '');
          setAddress(parsedProfile.result.address || '');
        }
      } catch (error) {
        console.error('Error parsing profile:', error);
      }
    } else if (user?.result) {
      // Use Redux user data if localStorage doesn't have profile
      setBusinessType(user.result.businessType || currentBusinessType || '');
      setCompanyName(user.result.companyName || '');
      setUserRole(user.result.role || 'owner');
      setOwnerName(
        user.result.ownerName ||
        (user.result.firstName && user.result.lastName
          ? `${user.result.firstName} ${user.result.lastName}`
          : user.result.firstName || '')
      );
      setEmail(user.result.email || '');
      setPhone(user.result.phone || '');
      setAddress(user.result.address || '');
    } else if (currentBusinessType) {
      setBusinessType(currentBusinessType);
    }
    
    setLoading(false);
  }, [user]);
  
  const handleSave = () => {
    if (!profile && !user?.result) return;
    
    // Create updated profile
    let updatedProfile = profile || { result: {} };
    
    if (!updatedProfile.result) {
      updatedProfile.result = {};
    }
    
    // Update fields
    updatedProfile.result.ownerName = ownerName;
    if (ownerName) {
      const nameParts = ownerName.split(' ');
      updatedProfile.result.firstName = nameParts[0] || '';
      updatedProfile.result.lastName = nameParts.slice(1).join(' ') || '';
    }
    updatedProfile.result.email = email;
    updatedProfile.result.phone = phone;
    updatedProfile.result.address = address;
    
    // Save to localStorage
    localStorage.setItem('profile', JSON.stringify(updatedProfile));
    
    // Save to session storage for active session
    if (businessType) {
      sessionStorage.setItem('businessType', businessType);
    }
    
    // Dispatch update to Redux
    dispatch(setUser(updatedProfile));
    
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };
  
  const onAdd = () => {};
  const onRead = () => {};
  const onUpdate = () => {};
  const onDelete = () => {};
  
  if (loading) {
    return (
      <div className="bg-gray-900 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-900 min-h-screen p-4 sm:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
          <h2 className="text-2xl font-bold text-white mb-6">{t('profile.myProfile')}</h2>
          
          <div className="space-y-6">
            {/* Business Type (read-only) */}
            <div>
              <label htmlFor="businessType" className="block text-sm font-medium text-gray-300 mb-2">
                {t('profile.businessType')}
              </label>
              <input
                type="text"
                id="businessType"
                readOnly
                className="w-full px-3 py-2 bg-gray-600 border border-gray-600 rounded-md text-gray-300 cursor-not-allowed"
                value={getBusinessTypeDisplayName(businessType) || businessType || ''}
              />
              <p className="text-sm text-gray-400 mt-1">
                {t('profile.businessTypeDescription')}
              </p>
            </div>
            
            {/* Company Name (read-only) */}
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-300 mb-2">
                {t('profile.companyName')}
              </label>
              <input
                type="text"
                id="companyName"
                readOnly
                className="w-full px-3 py-2 bg-gray-600 border border-gray-600 rounded-md text-gray-300 cursor-not-allowed"
                value={companyName}
              />
            </div>

            {/* Role (read-only) */}
            <div>
              <label htmlFor="userRole" className="block text-sm font-medium text-gray-300 mb-2">
                {t('profile.role')}
              </label>
              <input
                type="text"
                id="userRole"
                readOnly
                className="w-full px-3 py-2 bg-gray-600 border border-gray-600 rounded-md text-gray-300 cursor-not-allowed"
                value={getProfileRoleLabel(t, userRole)}
              />
            </div>
            
            {/* Owner Name */}
            <div>
              <label htmlFor="ownerName" className="block text-sm font-medium text-gray-300 mb-2">
                {t('profile.accountOwnerName')}
              </label>
              <input
                type="text"
                id="ownerName"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                placeholder={t('profile.accountOwnerName')}
              />
            </div>
            
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                {t('profile.email')}
              </label>
              <input
                type="email"
                id="email"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('profile.email')}
              />
            </div>
            
            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                {t('profile.phone')}
              </label>
              <input
                type="tel"
                id="phone"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t('profile.phone')}
              />
            </div>
            
            {/* Address */}
            <div>
              <label htmlFor="address" className="block text-sm font-medium text-gray-300 mb-2">
                {t('profile.address')}
              </label>
              <textarea
                id="address"
                rows="3"
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={t('profile.address')}
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-6">
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors font-medium"
            >
              {t('profile.save')}
            </button>
          </div>
          
          {saved && (
            <div className="mt-4 p-3 bg-green-600/20 border border-green-500/50 text-green-400 rounded-md">
              {t('profile.saved')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyProfileView;

